import React, { useState, useEffect } from 'react';
import { Select, Table, Switch } from 'antd';
import PropTypes from 'prop-types';
import { parseColumnsForRunInstanceResultListing } from './utils';
import { SearchSortAndFilter } from '../../../../components/ns/SearchSortAndFilter';
import Wait4Me from '../../../../components/Wait4Me';
import { ALERT_SOURCE_MAPPING } from '../../../../util/GlobalConstant';
import styles from './index.scss';
import {
  getAttributeByResourceCode,
  displaySwitchForTSNS,
  getModuleTypeByResourceCode
} from '../../../../util/miscellaneousUtils';
import { ROUTE_AMLS_SMART_SCREENING } from '../../../constants/globalConstants';
import { convertDateInputToEpoch } from '../../../../util/timezone';
import { isEmpty } from 'lodash';
import { btoaUTF8 } from '../../../common/utils';
import useAlertLevelConfigs from '../../../common/hooks/useAlertLevelConfig';
import { computeNextQueryState } from './filterSortUtils';

const alertResourceCode = 'amls:ui:common-alerts:read';

export const RunInstanceResult = ({
  runInstanceResultList,
  apiActions,
  GS,
  widgetItemOperations,
  moduleTypeHandler,
  moduleType,
  authentication,
  settings,
  alertLevels
}) => {
  const { getNSRunInstanceResultListData, getPageItemOperations } = apiActions;
  const { isLoading, data } = runInstanceResultList;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [extraQueryParameter, setExtraQueryParameter] = useState({
    filter: [],
    search: []
  });
  const [selectedModule, setSelectedModule] = useState('');
  const { getLevelColor, getLevelLabel } = useAlertLevelConfigs(alertLevels);

  useEffect(() => {
    if (
      [
        ALERT_SOURCE_MAPPING.NS,
        ALERT_SOURCE_MAPPING.PS,
        ALERT_SOURCE_MAPPING.PSRS,
        ALERT_SOURCE_MAPPING.TS
      ].includes(moduleType)
    ) {
      getNSRunInstanceResultListData({
        sourceIds: `${
          moduleType == ALERT_SOURCE_MAPPING.PSRS
            ? ALERT_SOURCE_MAPPING.PS
            : moduleType
        },`,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
        from: extraQueryParameter?.from
          ? convertDateInputToEpoch(extraQueryParameter?.from)
          : null,
        to: extraQueryParameter?.to
          ? convertDateInputToEpoch(extraQueryParameter?.to)
          : null,
        q: btoaUTF8(
          JSON.stringify({
            filter: [
              ...extraQueryParameter.filter,
              ...extraQueryParameter.search
            ],
            sort: extraQueryParameter?.sort
          })
        ),
        enableStats: false
      });
    } else {
      getNSRunInstanceResultListData({
        sourceIds: `${moduleType},`,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
        enableStats: false
      });
    }
  }, [pageSize, currentPage, extraQueryParameter, moduleType]);

  useEffect(() => {
    if (
      [
        ALERT_SOURCE_MAPPING.NS,
        ALERT_SOURCE_MAPPING.PS,
        ALERT_SOURCE_MAPPING.PSRS,
        ALERT_SOURCE_MAPPING.TS
      ].includes(moduleType)
    ) {
      getPageItemOperations({
        pageId: 'NS_IAD_ALERTS_LIST'
      });
    }
  }, []);

  const onPaginationChange = (page, newPageSize) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const alertIdClickHandler = (alertId) => {
    apiActions.navigateTo(
      `${ROUTE_AMLS_SMART_SCREENING}/investigation/${alertId}`,
      {
        sourceId: moduleType
      }
    );
  };

  const columns = parseColumnsForRunInstanceResultListing(
    GS && GS.widgetConfig,
    alertIdClickHandler,
    getLevelLabel,
    getLevelColor
  );

  const applyFilterHandler = (obj) => {
    setCurrentPage(1);
    setPageSize(5);
    setExtraQueryParameter(computeNextQueryState(obj, extraQueryParameter));
  };

  const renderLoadingBlock = () => {
    return (
      <div className={styles.loadingWrapper}>
        <Wait4Me />
      </div>
    );
  };

  const moduleToggleHandler = (value) => {
    moduleTypeHandler(value);
  };

  const renderFilter = () => {
    if (widgetItemOperations?.isLoading) renderLoadingBlock();
    else
      return (
        widgetItemOperations?.data !== null && (
          <SearchSortAndFilter
            applyFilterHandler={applyFilterHandler}
            widgetItemOperations={widgetItemOperations}
            filters={extraQueryParameter}
            alertLevels={alertLevels}
            moduleType={moduleType}
            sourceIds={`${moduleType},`}
          />
        )
      );
  };

  const modulesList = getModuleTypeByResourceCode(
    authentication,
    alertResourceCode
  );

  useEffect(() => {
    if (moduleType && modulesList?.length) {
      let validateModule = modulesList.find((m) => m.value === moduleType);
      setSelectedModule(isEmpty(validateModule) ? '' : validateModule?.value);
    }
  }, [moduleType, modulesList]);

  return (
    <div className={styles.runInstanceResult_wrapper}>
      <div className={styles.runInstanceResult_header}>
        <h3>Run Instance Result</h3>

        {displaySwitchForTSNS(authentication, alertResourceCode) ? (
          <Switch
            checkedChildren="Name"
            unCheckedChildren="Transaction"
            checked={moduleType === ALERT_SOURCE_MAPPING.NS ? true : false}
            onChange={moduleToggleHandler}
            disabled={
              getAttributeByResourceCode(authentication, alertResourceCode) !==
              '7,9'
            }
          />
        ) : (
          <Select
            value={selectedModule}
            placeholder="Please Select"
            onChange={moduleToggleHandler}
            style={{ paddingLeft: '15px', minWidth: '220px' }}>
            {modulesList?.map((module, index) => {
              if (module.value == '17') return;
              return (
                <Select.Option key={index} value={module.value}>
                  {module.label}
                </Select.Option>
              );
            })}
          </Select>
        )}
      </div>

      {settings?.nsSetting?.isLoading
        ? renderLoadingBlock()
        : GS?.isVisible && (
            <React.Fragment>
              {[
                ALERT_SOURCE_MAPPING.NS,
                ALERT_SOURCE_MAPPING.PS,
                ALERT_SOURCE_MAPPING.PSRS,
                ALERT_SOURCE_MAPPING.TS
              ].includes(moduleType) && renderFilter()}
              <Table
                id="runInstanceResultListing"
                className={styles.table}
                dataSource={data?.payload}
                columns={columns}
                loading={isLoading}
                rowKey="alertId"
                pagination={{
                  showSizeChanger: true,
                  responsive: false,
                  total: data?.total,
                  current: currentPage,
                  pageSize,
                  onChange: onPaginationChange,
                  pageSizeOptions: [5, 10, 25, 50, 100],
                  showTotal: (total) => `${total} Records`
                }}
                scroll={{ y: 400 }}
              />
            </React.Fragment>
          )}
    </div>
  );
};

RunInstanceResult.propTypes = {
  apiActions: PropTypes.object.isRequired,
  GS: PropTypes.object,
  runInstanceResultList: PropTypes.object.isRequired,
  widgetItemOperations: PropTypes.object,
  moduleTypeHandler: PropTypes.func,
  moduleType: PropTypes.string,
  alertLevels: PropTypes.array
};

export default RunInstanceResult;
